import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { CommentSchema, IssueCardSchema } from './issue.js';
import { NotificationSchema } from './notification.js';
import { SprintSchema } from './sprint.js';
import { StatusSchema, TransitionSchema } from './project.js';

/** События WebSocket (docs/05 §11). */

export const IssueCreatedEventSchema = z.object({
  type: z.literal('issue.created'),
  payload: z.object({ issue: IssueCardSchema, actorId: UuidSchema }),
});

export const IssueUpdatedEventSchema = z.object({
  type: z.literal('issue.updated'),
  payload: z.object({
    issueId: UuidSchema,
    key: z.string(),
    patch: z.record(z.unknown()),
    actorId: UuidSchema,
  }),
});

export const IssueDeletedEventSchema = z.object({
  type: z.literal('issue.deleted'),
  payload: z.object({ issueId: UuidSchema, key: z.string(), actorId: UuidSchema }),
});

export const IssueMovedEventSchema = z.object({
  type: z.literal('issue.moved'),
  payload: z.object({
    issueId: UuidSchema,
    key: z.string(),
    statusId: UuidSchema,
    sprintId: UuidSchema.nullable(),
    rank: z.string(),
    actorId: UuidSchema,
  }),
});

export const CommentCreatedEventSchema = z.object({
  type: z.literal('comment.created'),
  payload: z.object({ issueId: UuidSchema, comment: CommentSchema, actorId: UuidSchema }),
});

export const CommentUpdatedEventSchema = z.object({
  type: z.literal('comment.updated'),
  payload: z.object({ issueId: UuidSchema, comment: CommentSchema, actorId: UuidSchema }),
});

export const CommentDeletedEventSchema = z.object({
  type: z.literal('comment.deleted'),
  payload: z.object({ issueId: UuidSchema, commentId: UuidSchema, actorId: UuidSchema }),
});

export const AttachmentAddedEventSchema = z.object({
  type: z.literal('attachment.added'),
  payload: z.object({ issueId: UuidSchema, attachmentId: UuidSchema, actorId: UuidSchema }),
});

export const AttachmentDeletedEventSchema = z.object({
  type: z.literal('attachment.deleted'),
  payload: z.object({ issueId: UuidSchema, attachmentId: UuidSchema, actorId: UuidSchema }),
});

export const SprintUpdatedEventSchema = z.object({
  type: z.literal('sprint.updated'),
  payload: z.object({ sprint: SprintSchema, actorId: UuidSchema }),
});

export const WorkflowUpdatedEventSchema = z.object({
  type: z.literal('workflow.updated'),
  payload: z.object({
    statuses: z.array(StatusSchema),
    transitions: z.array(TransitionSchema),
    actorId: UuidSchema,
  }),
});

export const PresenceUpdatedEventSchema = z.object({
  type: z.literal('presence.updated'),
  payload: z.object({ issueId: UuidSchema, userIds: z.array(UuidSchema) }),
});

/**
 * Персональное уведомление (UC-ISS-10). Клиент получает его в комнату
 * проекта, но применяет только если `userId` совпадает с текущим: сервер не
 * держит персональных соединений (docs/05 §11).
 */
export const NotificationCreatedEventSchema = z.object({
  type: z.literal('notification.created'),
  payload: z.object({
    userId: UuidSchema,
    notification: NotificationSchema,
  }),
});

/** Wiki (docs/14 §8): тело страницы не гоняем — клиент перезапрашивает её сам. */
export const WikiPageChangedEventSchema = z.object({
  type: z.literal('wiki.page.changed'),
  payload: z.object({
    projectId: UuidSchema,
    pageId: UuidSchema,
    /** Что именно произошло: клиент решает, обновлять дерево или страницу. */
    change: z.enum(['created', 'updated', 'deleted', 'moved']),
    actorId: UuidSchema.nullable(),
  }),
});

export const PingEventSchema = z.object({ type: z.literal('ping') });
export const PongEventSchema = z.object({ type: z.literal('pong') });

export const ServerEventSchema = z.discriminatedUnion('type', [
  IssueCreatedEventSchema,
  IssueUpdatedEventSchema,
  IssueDeletedEventSchema,
  IssueMovedEventSchema,
  CommentCreatedEventSchema,
  CommentUpdatedEventSchema,
  CommentDeletedEventSchema,
  AttachmentAddedEventSchema,
  AttachmentDeletedEventSchema,
  SprintUpdatedEventSchema,
  WorkflowUpdatedEventSchema,
  PresenceUpdatedEventSchema,
  NotificationCreatedEventSchema,
  WikiPageChangedEventSchema,
  PongEventSchema,
]);

export type ServerEvent = z.infer<typeof ServerEventSchema>;
export type ServerEventType = ServerEvent['type'];

export const ClientEventSchema = z.discriminatedUnion('type', [
  PingEventSchema,
  z.object({ type: z.literal('presence.open'), payload: z.object({ issueId: UuidSchema }) }),
  z.object({ type: z.literal('presence.close'), payload: z.object({ issueId: UuidSchema }) }),
]);
export type ClientEvent = z.infer<typeof ClientEventSchema>;

/** Сообщение в конверте, как его отправляет сервер. */
export const EnvelopeSchema = z.object({
  type: z.string(),
  projectId: UuidSchema,
  ts: IsoDateTimeSchema,
  payload: z.unknown(),
});
export type Envelope = z.infer<typeof EnvelopeSchema>;
